import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const TrendingSection = ({ featuredEvent, trendingEvents, city }) => {
    const navigate = useNavigate();
    const [currentIndex, setCurrentIndex] = useState(0);

    if (!featuredEvent && (!trendingEvents || trendingEvents.length === 0)) return null;

    // Custom date formatter
    const formatDate = (dateStr) => {
        if (!dateStr) return '';

        let date;
        // Handle Firestore Timestamp
        if (dateStr && typeof dateStr.toDate === 'function') {
            date = dateStr.toDate();
        } else {
            date = new Date(dateStr);
        }

        // Check for invalid date
        if (isNaN(date.getTime())) return '';

        const dateOptions = { weekday: 'short', day: 'numeric', month: 'long', timeZone: 'UTC' };
        const timeOptions = { hour: 'numeric', minute: 'numeric', hour12: true, timeZone: 'UTC' };

        try {
            return `${date.toLocaleDateString('en-US', dateOptions)} | ${date.toLocaleTimeString('en-US', timeOptions)} Onwards`;
        } catch (e) {
            console.error("Date formatting error:", e);
            return '';
        }
    };

    // Navigation handlers
    const handlePrev = () => {
        if (trendingEvents && trendingEvents.length > 3) {
            setCurrentIndex((prev) => (prev === 0 ? Math.max(0, trendingEvents.length - 3) : Math.max(0, prev - 1)));
        }
    };

    const handleNext = () => {
        if (trendingEvents && trendingEvents.length > 3) {
            setCurrentIndex((prev) => (prev >= trendingEvents.length - 3 ? 0 : prev + 1));
        }
    };

    // Get visible events based on current index
    const visibleEvents = trendingEvents ? trendingEvents.slice(currentIndex, currentIndex + 3) : [];

    return (
        <div className="w-full bg-[#0A0A0A] py-8 sm:py-12 lg:py-16">
            <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-[120px]">

                {/* Mobile: Show Trending Text First */}
                <h2 className="lg:hidden text-[#17BD3B] font-['Poppins'] text-[16px] font-semibold tracking-normal mb-4">
                    <span className="live-dot mr-2" />#Trending
                </h2>

                <div className="flex flex-col lg:flex-row gap-6 lg:gap-12">

                    {/* LEFT COLUMN: Big Featured Poster - Smaller on mobile */}
                    {featuredEvent && (
                        <div
                            className="relative w-full sm:w-[280px] lg:w-[400px] xl:w-[450px] flex-shrink-0 aspect-[3/4] rounded-2xl overflow-hidden cursor-pointer shadow-2xl hover:shadow-[#17BD3B]/20 hover:scale-[1.02] transition-all duration-300 mx-auto sm:mx-0"
                            onClick={() => navigate(`/events/${featuredEvent.id}`)}
                        >
                            <img
                                src={featuredEvent.poster?.verticalUrl || featuredEvent.poster?.horizontalUrl}
                                alt={featuredEvent.name}
                                className="w-full h-full object-cover"
                            />
                        </div>
                    )}

                    {/* RIGHT COLUMN: Details & Recommendations */}
                    <div className="flex-1 flex flex-col">

                        {/* 1. Featured Event Details */}
                        <div className="mb-8 lg:mb-12">
                            {/* Desktop: Show Trending Text */}
                            <h2 className="hidden lg:block text-[#17BD3B] font-['Poppins'] text-[16px] font-semibold mb-3 tracking-normal">
                                <span className="live-dot mr-2" />#Trending
                            </h2>

                            <h1 className="text-white font-['Poppins'] text-[24px] sm:text-[32px] lg:text-[40px] font-semibold leading-[1.2] mb-4 lg:mb-6">
                                {featuredEvent?.name}
                            </h1>

                            <div className="flex flex-col gap-2 mb-4 lg:mb-6">
                                <p className="text-white font-['Poppins'] text-[14px] sm:text-[16px] font-normal leading-[24px]">
                                    {formatDate(featuredEvent?.startTime)}
                                </p>
                                <p className="text-white/90 font-['Poppins'] text-[14px] sm:text-[16px] font-normal leading-[24px]">
                                    {featuredEvent?.eventLocation?.locationText || 'Venue TBA'}
                                </p>
                            </div>

                            <button
                                onClick={() => navigate(`/events/${featuredEvent?.id}`)}
                                className="bg-[#17BD3B] hover:bg-[#15a834] text-white font-['Poppins'] font-semibold px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg transition-all duration-300 shadow-lg shadow-[#17BD3B]/20 hover:shadow-[#17BD3B]/40 hover:-translate-y-0.5 text-[14px] sm:text-[15px] btn-shine"
                            >
                                Book Now
                            </button>
                        </div>

                        {/* 2. Based on Preferences Section */}
                        <div className="mt-auto">
                            <div className="flex items-center justify-between mb-4 lg:mb-6">
                                <h3 className="text-white font-['Poppins'] text-[16px] sm:text-xl font-medium">
                                    Based on <span className="text-[#17BD3B]">Preferences</span>
                                </h3>

                                {/* Navigation Arrows - Now Functional */}
                                {trendingEvents && trendingEvents.length > 3 && (
                                    <div className="flex gap-3">
                                        <button
                                            onClick={handlePrev}
                                            className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
                                            aria-label="Previous events"
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M15 18l-6-6 6-6" />
                                            </svg>
                                        </button>
                                        <button
                                            onClick={handleNext}
                                            className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
                                            aria-label="Next events"
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M9 18l6-6-6-6" />
                                            </svg>
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Small Cards Row - re-keyed on arrow nav so cards fade in staggered */}
                            <div key={currentIndex} className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
                                {visibleEvents.map((event, i) => (
                                    <div
                                        key={event.id}
                                        className="group cursor-pointer fade-in-up transition-transform duration-300 hover:-translate-y-1.5 hover:scale-[1.03]"
                                        style={{ animationDelay: `${i * 80}ms` }}
                                        onClick={() => navigate(`/events/${event.id}`)}
                                    >
                                        <div className="relative aspect-[3/4] rounded-xl overflow-hidden border border-white/5 transition-all duration-300 group-hover:border-[#17BD3B]/40 group-hover:shadow-[0_14px_34px_-10px_rgba(23,189,59,0.35)]">
                                            <img
                                                src={event.poster?.verticalUrl || event.poster?.horizontalUrl}
                                                alt={event.name}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                    </div>

                </div>
            </div>
        </div>
    );
};

export default TrendingSection;
